/**
 * Sewing Efficiency — legacy `Win Dashboard Detail > Efficiency and Output Dashboard` and its
 * `Efficiency Detail` drill-through, consolidated into one report with governed views.
 *
 * One report id, one filter bar, one `POST /api/reports/sewing-efficiency/query`; `view` names
 * which query runs (§5.1). Only the views the screen is showing are fetched (§4.7).
 *
 * | view           | grain              | what it answers                          |
 * |----------------|--------------------|------------------------------------------|
 * | `summary`      | one row per line     | which lines are under target             |
 * | `daily`        | line × date          | how that line moved day to day           |
 * | `mo`           | line × date × MO     | which order the day was spent on         |
 * | `changeover`   | building × floor     | did the floor get through its changeovers|
 * | `totals`       | one row              | where the whole factory stands           |
 * | `totals-daily` | one row per date     | the shape the factory made getting there |
 *
 * `changeover` is legacy's CHANGEOVER QTY modal, ported as a tab — the one view that is not a grain
 * of the efficiency fact. `totals` and `totals-daily` are two queries because they are two
 * questions: `totals` states the number (range efficiency is `SUM(earned)/SUM(worked)`, not the
 * mean of the daily ratios), and the sparkline drawn from `totals-daily` shows only the shape.
 *
 * The summary view is fetched for the **whole factory**, with `floor` withheld: one small complete
 * result serves the chart, the spotlight and the table, which §4.3 permits over a complete result.
 * On `daily` and `mo` the same `?floor=` becomes a real predicate.
 *
 * Every filter, the active view, the sort, the metric, the chart mode, the chart grain, the row
 * chips, the open floor spotlight, the open trend and the By Line column filters travel in the URL
 * (§4.4). Nothing answer-defining is held in `useState` and nothing is persisted.
 *
 * What this page deliberately does not do: no arithmetic beyond scaling a fraction to a percentage
 * and the chart-only floor rollup (ratio of sums, §4.3 / §31); no status vocabulary and no invented
 * threshold (§4.6); no client-derived ratio in any totals row; no client classification — the chips
 * select on server-returned flags and decide membership of nothing. See `design-notes.md` for the
 * rest.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DataTable, type DataTableTotals } from '@/components/DataTable';
import { FilterBar } from '@/components/FilterBar';
import { ReportPage } from '@/components/ReportPage';
import { ButtonGroupSelector } from '@/components/report/ButtonGroupSelector';
import { ExportMenu } from '@/components/report/ExportMenu';
import { KioskToggle } from '@/components/report/KioskToggle';
import {
  headerFilterOptions,
  headerFiltersAreOffered,
  matchesHeaderFilters,
  withHeaderFilters,
  type HeaderFilterSelection,
} from '@/components/report/headerFilter';
import { useReportFilters, useReportSort } from '@/components/report/useReportFilters';
import { exportColumns, integer } from '@/components/report/groupedColumns';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useKioskMode } from '@/hooks/useKioskMode';
import { useReportQuery } from '@/hooks/useReportQuery';
import type {
  ReportColumn,
  ReportQueryRequest,
  ReportQueryResponse,
  SortSpec,
} from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

import { SewingTotalsCards, type SewingDailyTotals, type SewingTotals } from './cards';
import { EfficiencyChart } from './chart';
import {
  CHANGEOVER_PARAM,
  CHIP_SIDES,
  NO_CHIPS,
  SAMPLE_PARAM,
  applyChips,
  chipsAreOffered,
  encodeChipSides,
  parseChipSides,
  toggleSide,
  type ChipSelection,
  type ChipSide,
} from './chips';
import {
  CHANGEOVER_FILTER_FIELDS,
  DAILY_FILTER_FIELDS,
  MO_FILTER_FIELDS,
  SUMMARY_FILTER_FIELDS,
  SUMMARY_LIST_FILTER_FIELDS,
  filterOptionLabel,
  lineTypeSummaryColumns,
  lineTypeTotalValues,
  rowIdFor,
  sewingColumns,
  summaryTotalValues,
  type SewingRow,
} from './columns';
import {
  FloorSpotlightControls,
  FloorSpotlightPanels,
} from './spotlight';
import { buildingFloorOptions, parseBuildingFloor } from './spotlight-state';
import { LineTrend } from './trend';
import {
  GRAIN_PARAM,
  GRAIN_TABS,
  METRIC_PARAM,
  METRIC_TABS,
  MODE_PARAM,
  MODE_TABS,
  REFRESH_INTERVAL_MS,
  REFRESH_PARAM,
  VIEW_PARAM,
  VIEW_TABS,
  includesPpa,
  parseAutoRefresh,
  parseGrain,
  parseMetric,
  parseMode,
  parseView,
  resolveSewingFactory,
  sewingEfficiencyFilters,
  type ReportViewName,
} from './filters';

const REPORT_ID = 'sewing-efficiency';

/** Roughly the cadence the warehouse refreshes at (§6.3); refetching faster adds no freshness. */
const STALE_TIME = 5 * 60 * 1000;

/** Beyond this, `<DataTable>` must virtualize (§4.3). The daily and MO views reach it easily. */
const VIRTUALIZE_ABOVE = 1000;

const SORT_PARAM = 'sort';
const LINE_TYPE_SORT_PARAM = 'lineTypeSort';
const LINE_TYPE_FILTER_PREFIX = 'lineTypeFilter.';
const LINE_TYPE_FILTER_FIELDS = ['line_type_name', 'building'] as const;
const LINE_TYPE_DEFAULT_SORT: SortSpec[] = [
  { field: 'line_type_name', dir: 'asc' },
  { field: 'building', dir: 'asc' },
];
const FLOOR_PARAM = 'floor';
const SPOTLIGHT_PARAM = 'spotlight';
const FLOOR_TABLE_PARAM = 'floorTable';
/** Which line's daily trend is open under the summary chart. Presentation, like `floorTable`. */
const TREND_PARAM = 'trend';

const EMPTY_ROWS: SewingRow[] = [];
const EMPTY_COLUMNS: ReportColumn[] = [];

/** Column header filters, in the URL (§4.4) as one repeated param per field: `?lineFilter.x=…`. */
const HEADER_FILTER_PREFIX = 'lineFilter.';

/** A stable identity for "no column filter", so the row memo below does not churn every render. */
const NO_HEADER_FILTERS: HeaderFilterSelection = {};
const NO_LIST_FIELDS: ReadonlySet<string> = new Set();

/** Which columns each grid offers a filter on — its identity columns, per view. */
const FILTER_FIELDS: Record<ReportViewName, readonly string[]> = {
  summary: SUMMARY_FILTER_FIELDS,
  daily: DAILY_FILTER_FIELDS,
  mo: MO_FILTER_FIELDS,
  changeover: CHANGEOVER_FILTER_FIELDS,
};

/** Of those, the ones whose cell holds a comma-joined list. Only By Line has one. */
const LIST_FILTER_FIELDS: Record<ReportViewName, ReadonlySet<string>> = {
  summary: SUMMARY_LIST_FILTER_FIELDS,
  daily: NO_LIST_FIELDS,
  mo: NO_LIST_FIELDS,
  changeover: NO_LIST_FIELDS,
};

/** What one row of each grid is, for the "showing N of M" line. */
const ROW_NOUN: Record<ReportViewName, string> = {
  summary: 'lines',
  daily: 'line-days',
  mo: 'rows',
  changeover: 'floors',
};

/**
 * Whether a response is the whole answer — the gate on every whole-result client transform (§4.3).
 * Both halves matter: neither test replaces the other.
 */
function isWholeResponse(response: ReportQueryResponse<unknown> | undefined): boolean {
  return (
    response !== undefined && !response.truncated && response.rows.length === response.total
  );
}

const EMPTY_MESSAGE: Record<ReportViewName, string> = {
  summary: 'No sewing line recorded output or worked minutes in this period. Widen the dates, or clear the building and brand.',
  daily: 'No days recorded for these filters. Widen the dates, or clear the line.',
  mo: 'No manufacturing orders ran on these lines in this period. Widen the dates, or clear the line.',
  changeover: 'No changeovers were scheduled or recorded in this period. Widen the dates, or clear the building.',
};

const CAPTION: Record<ReportViewName, string> = {
  summary: 'Sewing efficiency and output by line',
  daily: 'Sewing efficiency and output by line and day',
  mo: 'Sewing output by line, day and manufacturing order',
  changeover: 'Changeovers planned and carried out, by building and floor',
};

export default function SewingEfficiencyReport() {
  const { user, status } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const view = parseView(searchParams.get(VIEW_PARAM));
  const { isKiosk } = useKioskMode();
  const metric = parseMetric(searchParams.get(METRIC_PARAM));
  const mode = parseMode(searchParams.get(MODE_PARAM));
  const grain = parseGrain(searchParams.get(GRAIN_PARAM));
  const autoRefresh = parseAutoRefresh(searchParams.get(REFRESH_PARAM));
  const showFloorTable = searchParams.get(FLOOR_TABLE_PARAM) === 'on';
  const trendLine = searchParams.get(TREND_PARAM);

  // In the URL so a shared link shows which chips were lit — "both" and "neither" are distinct
  // states even though neither filters anything.
  const chips = useMemo<ChipSelection>(
    () => ({
      sample: parseChipSides(searchParams.get(SAMPLE_PARAM)),
      changeover: parseChipSides(searchParams.get(CHANGEOVER_PARAM)),
    }),
    [searchParams],
  );

  // Rebuilt per factory (codes only mean anything inside one) and per view (`floor` is a predicate
  // on two views and a spotlight on the third).
  const factory = resolveSewingFactory(searchParams.get('factory'), user?.defaultFactory);
  const schema = useMemo(() => sewingEfficiencyFilters(factory, view), [factory, view]);
  const { values, isComplete, setValue } = useReportFilters(schema);

  // Write the factory into the URL, or a shared link resolves to the *recipient's* factory — a
  // different answer under the same address. Compared against the resolved value, not merely
  // absence, so a URL naming a withdrawn factory is corrected rather than left lying.
  useEffect(() => {
    if (status !== 'authenticated') return;
    if (searchParams.get('factory') === factory) return;
    setValue('factory', factory);
  }, [status, searchParams, factory, setValue]);

  const includePpa = includesPpa(values);
  /** Whether the reader has narrowed by brand — the one filter the changeover view cannot apply. */
  const brandIsSelected = (values['brand'] ?? '') !== '';
  // Read straight from the URL: a spotlight is one Building-Floor pair whose encoding contains a
  // comma, so it must not go through the comma-separated filter decoder.
  const spotlightFloors = useMemo(() => {
    const spotlight = searchParams.get(SPOTLIGHT_PARAM);
    return spotlight === null ? [] : [spotlight];
  }, [searchParams]);

  /**
   * The summary query covers the whole factory: `floor` is withheld so one complete result can
   * serve the overview chart, the spotlight and the table. On `daily` and `mo` it is sent, because
   * those grains are large and the floor genuinely narrows the query.
   */
  const requestFilters: ReportQueryRequest['filters'] = useMemo(() => {
    // `metric` is a declared filter — on summary it decides which lines are in the answer, not just
    // which columns are drawn. It binds inert on the other three views.
    const withMetric = { ...values, [METRIC_PARAM]: metric };
    if (view !== 'summary') return withMetric;
    return Object.fromEntries(Object.entries(withMetric).filter(([name]) => name !== FLOOR_PARAM));
  }, [view, values, metric]);

  /**
   * Grain order, per view — §4.6 and `DECISIONS.md` §28. Ascending, outermost grain first, and
   * every field is on that view's `sort:` whitelist so the export can repeat the default order.
   * Do not restore worst-efficiency-first, and do not make this depend on `metric`.
   */
  const defaultSort = useMemo<SortSpec[]>(() => {
    switch (view) {
      case 'summary':
        return [
          { field: 'factory', dir: 'asc' },
          { field: 'building', dir: 'asc' },
          { field: 'floor', dir: 'asc' },
          { field: 'show_line_no', dir: 'asc' },
        ];
      case 'daily':
        return [
          { field: 'tran_date', dir: 'asc' },
          { field: 'building', dir: 'asc' },
          { field: 'floor', dir: 'asc' },
          { field: 'line_name', dir: 'asc' },
        ];
      case 'mo':
        return [
          { field: 'tran_date', dir: 'asc' },
          { field: 'building', dir: 'asc' },
          { field: 'floor', dir: 'asc' },
          { field: 'line_name', dir: 'asc' },
          { field: 'mo', dir: 'asc' },
        ];
      case 'changeover':
        return [
          { field: 'factory', dir: 'asc' },
          { field: 'building', dir: 'asc' },
          { field: 'floor', dir: 'asc' },
        ];
    }
  }, [view]);

  const { sort, setSort } = useReportSort(defaultSort);
  const { sort: lineTypeSort, setSort: setLineTypeSort } = useReportSort(
    LINE_TYPE_DEFAULT_SORT,
    LINE_TYPE_SORT_PARAM,
  );

  const setUrlParam = useCallback(
    (name: string, value: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === null) next.delete(name);
          else next.set(name, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * Switching view drops the sort: the views have different columns, so one view's sort field is
   * usually not a column of the next — it would be silently ignored and its export would 400.
   */
  const selectView = useCallback(
    (next: ReportViewName) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          params.set(VIEW_PARAM, next);
          if (next !== 'summary') {
            const spotlight = parseBuildingFloor(params.get(SPOTLIGHT_PARAM));
            if (spotlight !== null) {
              params.set('building', spotlight.building);
              params.set(FLOOR_PARAM, spotlight.floor);
              params.delete(SPOTLIGHT_PARAM);
            }
          }
          params.delete(SORT_PARAM);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** Drill-through to a line's days. Pushed, not replaced, so Back returns to the floor. */
  const drillToLine = useCallback(
    (lineNo: string) => {
      setSearchParams((current) => {
        const params = new URLSearchParams(current);
        params.set(VIEW_PARAM, 'daily');
        params.set('line', lineNo);
        params.delete(SORT_PARAM);
        return params;
      });
    },
    [setSearchParams],
  );

  // Lazy by view (§4.7): the grain queries are expensive and nobody pays until they open one.
  const refetchInterval = autoRefresh ? REFRESH_INTERVAL_MS : undefined;

  const summary = useReportQuery<SewingRow>(REPORT_ID, requestFilters, {
    view: 'summary',
    enabled: isComplete && view === 'summary',
    staleTime: STALE_TIME,
    refetchInterval,
  });
  const daily = useReportQuery<SewingRow>(REPORT_ID, requestFilters, {
    view: 'daily',
    enabled: isComplete && view === 'daily',
    staleTime: STALE_TIME,
    refetchInterval,
  });
  const mo = useReportQuery<SewingRow>(REPORT_ID, requestFilters, {
    view: 'mo',
    enabled: isComplete && view === 'mo',
    staleTime: STALE_TIME,
    refetchInterval,
  });
  const changeover = useReportQuery<SewingRow>(REPORT_ID, requestFilters, {
    view: 'changeover',
    enabled: isComplete && view === 'changeover',
    staleTime: STALE_TIME,
    refetchInterval,
  });
  // The factory headline figures, aggregated in ADB over the same statement `summary` runs, so a
  // card cannot disagree with the chart beneath it. No sort: `sort:` is empty in `report.yaml`.
  // Deliberately not lazy by view (§4.7) — the cards are on every tab, and all four tabs send the
  // same filters, so TanStack serves the second from cache.
  const totals = useReportQuery<SewingTotals>(REPORT_ID, requestFilters, {
    view: 'totals',
    enabled: isComplete,
    staleTime: STALE_TIME,
    refetchInterval,
  });
  // Sorted explicitly rather than trusting the query's ordering: a series read right to left is
  // not a trend.
  const totalsDaily = useReportQuery<SewingDailyTotals>(REPORT_ID, requestFilters, {
    view: 'totals-daily',
    enabled: isComplete,
    staleTime: STALE_TIME,
    refetchInterval,
  });

  // Everything the shell shows describes the view on screen, never whichever query answered first.
  const active =
    view === 'summary'
      ? summary
      : view === 'daily'
        ? daily
        : view === 'changeover'
          ? changeover
          : mo;
  const rows = active.data?.rows ?? EMPTY_ROWS;
  // Memoized: a fresh `[]` each render would rebuild every column definition on every keystroke.
  const responseColumns = useMemo(() => active.data?.columns ?? EMPTY_COLUMNS, [active.data]);
  const available = useMemo(
    () => new Set(responseColumns.map((column) => column.field)),
    [responseColumns],
  );

  /**
   * Whether the summary result is the factory's whole picture. Every whole-result re-layout below
   * — the spotlight, its ranking, its transposed table — is gated on this (§4.3).
   */
  const summaryIsWhole = isWholeResponse(summary.data);

  // The same gate on the two card queries. The rule is completeness, not plausibility (§4.3): both
  // fail closed — no series drawn, no at-or-over-target reading taken.
  const totalsAreWhole = isWholeResponse(totals.data);
  const dailySeries = isWholeResponse(totalsDaily.data)
    ? (totalsDaily.data?.rows ?? null)
    : null;

  // Chips only over a complete summary: narrowing a capped result presents part as the whole (§4.3).
  const offerChips = view === 'summary' && chipsAreOffered(summary.data);
  const activeChips = offerChips ? chips : NO_CHIPS;

  /**
   * The rows every panel on the summary screen draws from — applied once, so the chart, the
   * rankings, the floor table and the main table cannot disagree about which lines are on screen.
   */
  const visibleRows = useMemo(
    () => (view === 'summary' ? applyChips(rows, activeChips, metric) : rows),
    [view, rows, activeChips, metric],
  );

  /** The spotlighted Building-Floor, parsed once so chart, trend gate and panels agree. */
  const chosenFloor = useMemo(
    () => parseBuildingFloor(spotlightFloors[0] ?? null),
    [spotlightFloors],
  );

  /** Lines on the chosen floor, or every chipped line when none is chosen — what the chart draws. */
  const chartRows = useMemo(() => {
    if (chosenFloor === null) return visibleRows;
    return visibleRows.filter(
      (row) =>
        String(row['building'] ?? '') === chosenFloor.building &&
        String(row['floor'] ?? '') === chosenFloor.floor,
    );
  }, [visibleRows, chosenFloor]);

  /**
   * Floor grain only when the factory is in view and the summary is whole. Spotlight forces Line
   * so a single-floor Floor chart is never one lonely bar (spec §4).
   */
  const plottingFloor =
    view === 'summary' && grain === 'floor' && chosenFloor === null && summaryIsWhole;

  // Withheld when `?trend=` names a line no longer among the bars, rather than showing a ghost.
  // Trends are line-only: never open under a floor-grain chart.
  const trendRow =
    plottingFloor || trendLine === null
      ? undefined
      : chartRows.find((row) => String(row['line_no']) === trendLine);
  const trendLabel = String(trendRow?.['show_line_no'] ?? trendLine ?? '');

  /**
   * The column header filters, and the rows they leave. Table-only by design — chips narrow every
   * panel, these narrow the table. Withheld over a capped result, or the option lists would be a
   * partial set of buildings offered as the set (§4.3).
   */
  const filterFields = FILTER_FIELDS[view];
  const listFilterFields = LIST_FILTER_FIELDS[view];

  const headerFilters = useMemo<HeaderFilterSelection>(() => {
    const selection: Record<string, string[]> = {};
    for (const field of filterFields) {
      selection[field] = searchParams.getAll(`${HEADER_FILTER_PREFIX}${field}`);
    }
    return selection;
  }, [searchParams, filterFields]);

  const offerHeaderFilters = filterFields.length > 0 && headerFiltersAreOffered(active.data);
  const activeHeaderFilters = offerHeaderFilters ? headerFilters : NO_HEADER_FILTERS;

  // From `rows`, not `visibleRows`: options derived from chipped rows would let a value vanish
  // from its own dropdown, with no way for the reader to select it back.
  const headerFilterOptionsByField = useMemo(
    () => headerFilterOptions(rows, filterFields, listFilterFields),
    [rows, filterFields, listFilterFields],
  );

  const onHeaderFilterChange = useCallback(
    (field: string, values: string[]) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          const param = `${HEADER_FILTER_PREFIX}${field}`;
          next.delete(param);
          for (const value of values) next.append(param, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** What the table draws: the chipped rows, narrowed again by the column headers. */
  const tableRows = useMemo(
    () =>
      offerHeaderFilters
        ? visibleRows.filter((row) =>
            matchesHeaderFilters(row, filterFields, activeHeaderFilters, listFilterFields),
          )
        : visibleRows,
    [offerHeaderFilters, visibleRows, activeHeaderFilters, filterFields, listFilterFields],
  );

  const showLineTypeSummary =
    view === 'summary' &&
    summaryIsWhole &&
    new Set(tableRows.map((row) => String(row['line_type_name'] ?? 'Conventional'))).size > 1;
  const lineTypeHeaderFilters = useMemo<HeaderFilterSelection>(
    () => Object.fromEntries(
      LINE_TYPE_FILTER_FIELDS.map((field) => [field, searchParams.getAll(`${LINE_TYPE_FILTER_PREFIX}${field}`)]),
    ),
    [searchParams],
  );
  const lineTypeFilterOptions = useMemo(
    () => headerFilterOptions(tableRows, LINE_TYPE_FILTER_FIELDS, NO_LIST_FIELDS),
    [tableRows],
  );
  const lineTypeSelectedLines = useMemo(
    () => tableRows.filter((row) =>
      matchesHeaderFilters(row, LINE_TYPE_FILTER_FIELDS, lineTypeHeaderFilters, NO_LIST_FIELDS),
    ),
    [tableRows, lineTypeHeaderFilters],
  );
  const onLineTypeHeaderFilterChange = useCallback(
    (field: string, values: string[]) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        const param = `${LINE_TYPE_FILTER_PREFIX}${field}`;
        next.delete(param);
        for (const value of values) next.append(param, value);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );
  const lineTypeFilters: ReportQueryRequest['filters'] = useMemo(
    () => ({
      ...requestFilters,
      line: [...new Set(lineTypeSelectedLines.map((row) => String(row['line_no'] ?? '')))].filter(Boolean),
    }),
    [requestFilters, lineTypeSelectedLines],
  );
  const lineTypeSummary = useReportQuery<SewingRow>(REPORT_ID, lineTypeFilters, {
    view: 'line-type-summary',
    enabled: isComplete && showLineTypeSummary && lineTypeSelectedLines.length > 0,
    staleTime: STALE_TIME,
    refetchInterval,
  });
  const lineTypeOptionLabel = useMemo(
    () => filterOptionLabel(lineTypeSummary.data?.columns ?? EMPTY_COLUMNS),
    [lineTypeSummary.data],
  );
  const lineTypeColumns = useMemo(
    () => withHeaderFilters(
      lineTypeSummaryColumns(lineTypeSummary.data?.columns ?? EMPTY_COLUMNS, metric, includePpa),
      LINE_TYPE_FILTER_FIELDS,
      lineTypeHeaderFilters,
      lineTypeFilterOptions,
      onLineTypeHeaderFilterChange,
      lineTypeOptionLabel,
    ),
    [lineTypeSummary.data, metric, includePpa, lineTypeHeaderFilters, lineTypeFilterOptions,
      onLineTypeHeaderFilterChange, lineTypeOptionLabel],
  );
  const lineTypeRows = lineTypeSelectedLines.length === 0
    ? EMPTY_ROWS
    : (lineTypeSummary.data?.rows ?? EMPTY_ROWS);
  const lineTypeAvailable = useMemo(
    () => new Set((lineTypeSummary.data?.columns ?? EMPTY_COLUMNS).map((column) => column.field)),
    [lineTypeSummary.data],
  );
  const lineTypeTotals: DataTableTotals | undefined = isWholeResponse(lineTypeSummary.data)
    && lineTypeRows.length > 0
    ? {
        labelColumn: 'line_type_name',
        label: 'Total',
        values: lineTypeTotalValues(metric, includePpa, lineTypeAvailable, lineTypeRows[0]),
        format: (value) => integer.format(Math.round(value)),
      }
    : undefined;

  /**
   * The Building-Floor buttons, off the **unchipped** rows: a floor whose lines were all chipped
   * out still exists and still needs its button. Withheld when the result is capped (§4.3).
   */
  const floorButtons = useMemo(() => buildingFloorOptions(rows), [rows]);

  /** Opens a line's daily trend under the chart. Same URL param as the Daily trend select. */
  const openLineTrend = useCallback(
    (lineNo: string) => {
      setUrlParam(TREND_PARAM, lineNo);
    },
    [setUrlParam],
  );

  /**
   * Spotlight a Building–Floor (or clear it). Selecting a floor is a line drill-in: Grain switches
   * to Line so the control matches the plot (floor bar click and Building–Floor buttons alike).
   */
  const selectFloor = useCallback(
    (floor: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (floor === null) {
            next.delete(SPOTLIGHT_PARAM);
          } else {
            next.set(SPOTLIGHT_PARAM, floor);
            // Default grain is line — drop the param rather than write `line`.
            next.delete(GRAIN_PARAM);
          }
          // A floor change can take the open trend's line off the chart.
          next.delete(TREND_PARAM);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * Choosing Floor clears any spotlight so Floor bars are visible. Choosing Line only updates the
   * grain preference (spotlight stays if the reader already drilled into a floor).
   */
  const selectGrain = useCallback(
    (next: 'line' | 'floor') => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next === 'line') {
            params.delete(GRAIN_PARAM);
          } else {
            params.set(GRAIN_PARAM, 'floor');
            params.delete(SPOTLIGHT_PARAM);
            params.delete(TREND_PARAM);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** Labels a filter option the way its own column renders its cells — dates, in practice. */
  const optionLabel = useMemo(() => filterOptionLabel(responseColumns), [responseColumns]);

  const columns = useMemo(() => {
    const base = sewingColumns({
      columns: responseColumns,
      view,
      metric,
      includePpa,
      onDrillToLine: drillToLine,
    });
    if (!offerHeaderFilters) {
      return base;
    }
    return withHeaderFilters(
      base,
      filterFields,
      activeHeaderFilters,
      headerFilterOptionsByField,
      onHeaderFilterChange,
      // Labels only — the value matched against and put in the URL is unchanged.
      optionLabel,
    );
  }, [
    filterFields,
    responseColumns,
    optionLabel,
    view,
    metric,
    includePpa,
    drillToLine,
    offerHeaderFilters,
    activeHeaderFilters,
    headerFilterOptionsByField,
    onHeaderFilterChange,
  ]);

  /**
   * The layout the export reproduces (§5.9), derived from the very columns the grid renders —
   * a separate list would drift, and nobody looks at it until a spreadsheet is wrong.
   */
  const exportLayout = useMemo(() => exportColumns(columns), [columns]);

  /**
   * The bottom totals row, on every table — a totals row is the default and its absence is what
   * needs a reason. Gated on the active view's own completeness (§4.3), which does real work
   * because `daily` and `mo` can genuinely be truncated.
   *
   * Which cells fill is decided in `columns.tsx` via `meta.total`, not here. Only By Line fills its
   * efficiency cells, and only by *reading* the `totals` view — never by averaging the column. They
   * are withheld the moment a chip or a column filter narrows the table, since `totals` answers for
   * every line and not for a selection. By Day gets no equivalent: `daily` applies row-visibility
   * rules `totals` does not, so the two populations are not interchangeable.
   */
  const tableTotals = useMemo<DataTableTotals | undefined>(() => {
    const complete = view === 'summary' ? summaryIsWhole : isWholeResponse(active.data);
    if (!complete) {
      return undefined;
    }
    const narrowed = tableRows.length !== rows.length;
    return {
      // The first column of each layout, so "Total" reads at the start of the row.
      labelColumn: view === 'summary' || view === 'changeover' ? 'building' : 'tran_date',
      label: 'Total',
      values:
        view !== 'summary' || narrowed || !totalsAreWhole
          ? undefined
          : summaryTotalValues(metric, includePpa, available, totals.data?.rows[0] ?? null),
      // Every summed column is pieces or minutes, so the row formats as whole figures.
      format: (value) => integer.format(Math.round(value)),
    };
  }, [
    view,
    summaryIsWhole,
    active.data,
    tableRows.length,
    rows.length,
    totalsAreWhole,
    metric,
    includePpa,
    available,
    totals.data,
  ]);

  const viewTabs = (
    <div role="tablist" aria-label="View" className="flex gap-0.5 border-b border-border">
      {VIEW_TABS.map((tab) => (
        <Button
          key={tab.value}
          role="tab"
          variant="ghost"
          aria-selected={view === tab.value}
          onClick={() => {
            selectView(tab.value);
          }}
          className={cn(
            'h-8 rounded-none border-b-2 border-transparent px-3 text-sm',
            view === tab.value && 'border-primary font-medium text-foreground',
          )}
        >
          {tab.label}
        </Button>
      ))}
    </div>
  );

  /**
   * The presentation controls. None of them is a filter: each chooses between columns the server
   * already computed or selects over rows already fetched, so none is sent to the API. Anything
   * that narrows rows belongs in the filter bar instead — that is why Measured by lives there.
   */
  const toolbar = (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        {view === 'summary' ? (
          <ButtonGroupSelector
            label="Chart"
            options={MODE_TABS}
            value={mode}
            onChange={(next) => {
              setUrlParam(MODE_PARAM, next);
            }}
          />
        ) : null}
        {/* Grain is presentation over a complete summary only — same gate as chips (§4.3 / §31). */}
        {view === 'summary' && summaryIsWhole ? (
          <ButtonGroupSelector
            label="Grain"
            options={GRAIN_TABS}
            value={grain}
            onChange={selectGrain}
          />
        ) : null}
        {/* Summary-only: these select over the summary's complete row set. Withheld when it is
            capped — a partial list of floors offered as the set is worse than no control (§4.3). */}
        {view === 'summary' && summaryIsWhole && floorButtons.length > 0 ? (
          <FloorSpotlightControls
            floors={floorButtons}
            selected={spotlightFloors}
            onSelectFloor={selectFloor}
            chartLines={chartRows}
            trendLine={trendLine}
            onTrendLineChange={(lineNo) => {
              setUrlParam(TREND_PARAM, lineNo);
            }}
            showTable={showFloorTable}
            onShowTableChange={(open) => {
              setUrlParam(FLOOR_TABLE_PARAM, open ? 'on' : null);
            }}
          />
        ) : null}
        {/* Not filters: they select over rows already fetched, and are withheld when incomplete. */}
        {offerChips ? (
          <div className="ml-auto flex flex-wrap items-end gap-x-4 gap-y-2">
            <ChipPair
              label="Sample production"
              subject="sample production"
              sides={chips.sample}
              onToggle={(side) => {
                setUrlParam(SAMPLE_PARAM, encodeChipSides(toggleSide(chips.sample, side)));
              }}
            />
            <ChipPair
              label="Changeover"
              subject="a changeover"
              sides={chips.changeover}
              onToggle={(side) => {
                setUrlParam(CHANGEOVER_PARAM, encodeChipSides(toggleSide(chips.changeover, side)));
              }}
            />
          </div>
        ) : null}
      </div>
      {/* Says which surface each narrowing applies to: a chip redraws every panel, a column filter
          narrows only the table, and "showing 12 of 82" alone reads as the chart being wrong. */}
      {tableRows.length !== rows.length ? (
        <p className="text-xs text-muted-foreground">
          Showing {integer.format(tableRows.length)} of {integer.format(rows.length)}{' '}
          {ROW_NOUN[view]}
          {view === 'summary' && tableRows.length !== visibleRows.length
            ? '. The chart and floor panels above ignore the column filters'
            : null}
          . Export covers everything these filters return, not this selection.
        </p>
      ) : null}
      {/* Two things a reader cannot infer: why a floor can read plan 0 against actual 1, and that
          the Brand filter genuinely does not apply here. Otherwise both look like bugs. */}
      {view === 'changeover' ? (
        <p className="text-xs text-muted-foreground">
          Plan counts changeovers scheduled in this period. Actual counts scheduled changeovers the
          line was seen to carry out — it produced output against the new order within 8 days — so a
          changeover planned earlier and done in this period shows as actual with no plan beside it.
          {brandIsSelected
            ? ' The Brand filter does not narrow this table: a changeover is a line event between two orders and carries no single brand.'
            : ''}
        </p>
      ) : null}
    </div>
  );

  /**
   * The headline cards, on every tab and in full screen. They are the factory's figures for the
   * filters in the bar, not a summary of whichever grain the table is showing. The kiosk wrapper
   * supplies the gutter `<ReportPage>`'s kiosk branch does not, and `flex-none` keeps the row's
   * height in that branch's `h-dvh` flex column.
   */
  const cards = (
    <SewingTotalsCards
      totals={totals.data?.rows[0] ?? null}
      isLoading={totals.isLoading}
      daily={dailySeries}
      dailyIsLoading={totalsDaily.isLoading}
      canCompare={totalsAreWhole}
      metric={metric}
      mode={mode}
      includePpa={includePpa}
    />
  );
  const headlineCards = isKiosk ? <div className="flex-none px-6 py-3">{cards}</div> : cards;

  const chartScope = plottingFloor
    ? `every floor at ${factory}`
    : chosenFloor === null
      ? `every line at ${factory}`
      : `${factory} ${chosenFloor.building}-${chosenFloor.floor}`;

  const summaryChart =
    view === 'summary' ? (
      <div className="flex flex-col gap-3">
        <EfficiencyChart
          rows={chartRows}
          mode={mode}
          metric={metric}
          includePpa={includePpa}
          available={available}
          grain={plottingFloor ? 'floor' : 'line'}
          scope={chartScope}
          // Tall enough for a factory-wide axis (~150 bars) without burying the rest of the summary.
          heightClass="h-72"
          onSelectLine={plottingFloor ? undefined : openLineTrend}
          onSelectFloor={plottingFloor ? selectFloor : undefined}
        />

        {trendRow === undefined ? null : (
          <LineTrend
            // Keyed on the line, so switching remounts rather than showing the previous series.
            key={trendLine}
            lineNo={String(trendRow['line_no'])}
            lineLabel={trendLabel}
            filters={requestFilters}
            mode={mode}
            metric={metric}
            includePpa={includePpa}
            staleTime={STALE_TIME}
            refetchInterval={refetchInterval}
            onClose={() => {
              setUrlParam(TREND_PARAM, null);
            }}
          />
        )}

        <FloorSpotlightPanels
          chartRows={chartRows}
          isComplete={summaryIsWhole}
          factory={factory}
          selected={spotlightFloors}
          metric={metric}
          includePpa={includePpa}
          available={available}
          showTable={showFloorTable}
          showRanking={!plottingFloor}
          onSelectLine={openLineTrend}
        />
      </div>
    ) : undefined;

  return (
    <ReportPage
      title="Sewing Efficiency"
      description="Efficiency, output and target for every sewing line — by line, by day, and by manufacturing order."
      kiosk={isKiosk}
      // In kiosk the tabs and filter bar are gone, so the heading names the view and the factory.
      kioskHeading={`${factory} · ${VIEW_TABS.find((tab) => tab.value === view)?.label ?? ''}`}
      filters={
        // Withheld in kiosk, which also unmounts the bar's live option queries rather than leaving
        // them refetching behind a layout nobody can see.
        isKiosk ? undefined : (
          <FilterBar
            schema={schema}
            afterFilters={
              // A fragment, not a wrapper div: wrapped, these two are one flex item and take a line.
              <>
                {/* `metric` is a declared, required filter — it narrows rows, not just columns — so
                    it belongs here rather than in the toolbar. Instant-apply, not staged behind the
                    bar's Query button. */}
                <ButtonGroupSelector
                  label="Measured by"
                  options={METRIC_TABS}
                  value={metric}
                  onChange={(next) => {
                    setUrlParam(METRIC_PARAM, next);
                  }}
                />
                {/* h-9 so it shares the baseline of the filter controls beside it. */}
                <label
                  htmlFor="sewing-auto-refresh"
                  title="Refetches every 5 minutes"
                  className="flex h-9 items-center gap-2 text-xs font-medium text-muted-foreground"
                >
                  Auto refresh
                  <Switch
                    id="sewing-auto-refresh"
                    checked={autoRefresh}
                    onCheckedChange={(checked) => {
                      setUrlParam(REFRESH_PARAM, checked ? 'on' : null);
                    }}
                  />
                </label>
              </>
            }
            actions={
              <>
                <ExportMenu
                  reportId={REPORT_ID}
                  // Report intent, never the rows on screen (§5.9): the server re-runs this view.
                  request={{ view, filters: requestFilters, sort }}
                  // …and the layout it is rendered in, so the file is the table the reader exported.
                  columns={exportLayout}
                  disabled={!isComplete}
                />
                <KioskToggle />
              </>
            }
          />
        )
      }
      summary={headlineCards}
      views={viewTabs}
      toolbar={toolbar}
      chart={summaryChart}
      isLoading={active.isLoading}
      error={active.error}
      generatedAt={active.data?.generatedAt}
      // Null for this report — no source publishes a refresh watermark, and query time is not
      // freshness (§5.7), so no freshness line is shown at all.
      dataAsOf={active.data?.dataAsOf}
      truncated={active.data?.truncated === true}
      table={
        <div className="flex min-w-0 flex-col gap-4">
        {showLineTypeSummary && (
          <section className="flex flex-col gap-2" aria-labelledby="sewing-line-type-summary">
            <h2 id="sewing-line-type-summary" className="text-sm font-medium">
              By line type
            </h2>
            {lineTypeSummary.error ? (
              <p role="alert" className="text-sm text-destructive">
                Line type summary could not load. Refresh the page to try again.
              </p>
            ) : (
              <DataTable
                columns={lineTypeColumns}
                data={lineTypeRows}
                isLoading={lineTypeSummary.isLoading}
                sort={lineTypeSort}
                onSortChange={setLineTypeSort}
                totals={lineTypeTotals}
                paginate={false}
                getRowId={(row) => `${String(row['line_type_name'] ?? '')}|${String(row['building'] ?? '')}`}
                caption="Sewing efficiency and output by line type"
                emptyMessage="No line type totals are available for these filters."
              />
            )}
          </section>
        )}
        <DataTable
          columns={columns}
          data={tableRows}
          isLoading={active.isLoading}
          sort={sort}
          onSortChange={setSort}
          getRowId={rowIdFor(view)}
          totals={tableTotals}
          // The pager needs this separately: its row count is a whole-result claim of its own (§4.3).
          truncated={active.data?.truncated === true}
          virtualized={tableRows.length > VIRTUALIZE_ABOVE}
          caption={CAPTION[view]}
          emptyMessage={EMPTY_MESSAGE[view]}
        />
        </div>
      }
    />
  );
}

/**
 * One pair of row chips — legacy's "with X" / "without X" checkboxes. Two independent toggles and
 * not a switch: a switch cannot express the *without* side, nor both-on versus both-off, which are
 * distinct states even though neither filters anything (`chips.ts` holds the rule).
 */
function ChipPair({
  label,
  subject,
  sides,
  onToggle,
}: {
  label: string;
  /** Completes "Lines with …" / "Lines without …" for the accessible name. */
  subject: string;
  sides: readonly ChipSide[];
  onToggle: (side: ChipSide) => void;
}) {
  const id = `chips-${subject.replace(/\s+/g, '-')}`;
  return (
    <div className="flex flex-col gap-1">
      {/* A span, not a <label>: a <label> with no single form field to point at is not a label. */}
      <span id={id} className="text-xs font-medium">
        {label}
      </span>
      <div role="group" aria-labelledby={id} className="flex gap-1 rounded-md border bg-muted p-0">
        {CHIP_SIDES.map((side) => {
          const pressed = sides.includes(side);
          return (
            <Button
              key={side}
              type="button"
              size="sm"
              variant={pressed ? 'default' : 'ghost'}
              aria-pressed={pressed}
              // A screen reader reaches the button before the group name, so it needs the sentence.
              aria-label={`Lines ${side} ${subject}`}
              className="h-7 px-2.5 text-xs"
              onClick={() => {
                onToggle(side);
              }}
            >
              {side === 'with' ? 'With' : 'Without'}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
